import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  changeCommentTypeStatus,
  commentTypesListQuery,
  commentTypesShellQuery,
  createCommentType,
  removeCommentType,
  updateCommentType,
  type CommentTypeRow,
} from '../../api/settingsEaseedeskCommentTypes'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Comments types.
 *
 * Element cu element fata de `version-3/easeedesk_settings/comments/settings/
 * index.php` + `list.php`, si gest cu gest fata de `index-1.js` si `list-1.js`:
 * lista cu chenar colorat, comutatorul care salveaza singur, si cele doua
 * ferestre (creare si editare).
 *
 * FELURILE COMUNE (`default = 1`) N-AU BUTOANE, ca in macheta: se pot doar
 * aprinde si stinge. Numele lor e al tuturor instantelor, deci nu se schimba de
 * la un furnizor.
 */
export function CommentTypesPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(commentTypesShellQuery())
  const list = useQuery(commentTypesListQuery())
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<CommentTypeRow | null>(null)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'comment_types'] })
  }

  const remove = useMutation({
    mutationFn: (type: number) => removeCommentType(type),
    onSuccess: () => {
      toast('Comment type removed.', 'Success')
      reload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  const status = useMutation({
    mutationFn: ({ type, on }: { type: number; on: boolean }) => changeCommentTypeStatus(type, on),
    onSuccess: () => reload(),
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed what="Comments types" error={shell.error} onRetry={() => void shell.refetch()} />
  }
  if (!shell.data) return <Spinner />

  const row = (type: CommentTypeRow, editable: boolean) => (
    <li
      className="list-li"
      key={type.ID}
      style={{
        padding: '10px 20px',
        marginBottom: 4,
        borderRadius: 4,
        border: `1px solid ${editable ? type.type_color : '#ddd'}`,
        borderLeft: `6px solid ${editable ? type.type_color : '#DDD'}`,
        borderRight: `6px solid ${editable ? type.type_color : '#DDD'}`,
      }}
    >
      <div className="row">
        <div className="col-md-8">
          <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
            {type.comment_name}
          </h5>
        </div>
        <div className="col-md-4">
          <div className="change_type_status pull-right" style={{ marginLeft: 20 }}>
            <input
              type="checkbox"
              checked={type.active === 1}
              onChange={(e) => status.mutate({ type: type.ID, on: e.target.checked })}
            />
          </div>
          {editable && (
            <div className="btn-group pull-right" role="group" aria-label="controls">
              <button
                type="button"
                className="btn btn-danger btn-xs delete-type"
                onClick={() => {
                  if (!window.confirm('Are you sure?')) return
                  remove.mutate(type.ID)
                }}
              >
                <i className="fas fa-trash" aria-hidden="true"></i>
              </button>{' '}
              <button type="button" className="btn btn-primary btn-xs edit-type" onClick={() => setEdited(type)}>
                <i className="fas fa-pencil-alt" aria-hidden="true"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </li>
  )

  return (
    <div>
      <h4>
        <strong>Comments</strong> types
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit comments <strong>types</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create comment type
      </button>
      <hr />

      <div className="list-group">
        {list.error ? (
          <LoadFailed what="The comment types" error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner />
        ) : list.data.default_comment_types.length === 0 && list.data.comment_types.length === 0 ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> No comment types.
          </div>
        ) : (
          <ul>
            {list.data.default_comment_types.map((type) => row(type, false))}
            {list.data.comment_types.map((type) => row(type, true))}
          </ul>
        )}
      </div>

      <div className="well corner-flip flip-bg-white" data-color="inverse">
        {/* `od-note-title`: `body.od-console h4:not([class])` scrie titlurile de
            ecran cu serif de 22px. Caseta asta e o nota, nu un nume de ecran, si
            `.well` nu e intre exceptiile din foaie (`.portlet`, `.panel`,
            `section.settings`, `*-card`) - deci "NOTE" iesea mai mare decat
            randul de sub el. O clasa, oricare, scoate `h4` de sub regula. */}
        <h4 className="od-note-title">
          <i className="fas fa-exclamation-triangle"></i> <strong>NOTE</strong>{' '}
        </h4>
        <p>
          You can create NEW comment types by clicking <strong>CREATE</strong> button.
        </p>
        <div className="flip"></div>
      </div>

      <CommentTypeModal open={creating} type={null} onClose={() => setCreating(false)} onSaved={reload} />
      <CommentTypeModal open={edited !== null} type={edited} onClose={() => setEdited(null)} onSaved={reload} />
    </div>
  )
}

/** `newCommentTypeModal` si `editCommentTypeModal`: nume si culoare. */
export function CommentTypeModal({
  open,
  type,
  onClose,
  onSaved,
}: {
  open: boolean
  type: CommentTypeRow | null
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [color, setColor] = useState('#DDDDDD')

  useEffect(() => {
    if (!open) return
    setName(type?.comment_name ?? '')
    setColor(type?.type_color ?? '#DDDDDD')
  }, [open, type])

  const save = useMutation({
    mutationFn: () =>
      type === null ? createCommentType(name.trim(), color) : updateCommentType(type.ID, name.trim(), color),
    onSuccess: () => {
      toast(type === null ? 'New comment type created.' : 'Settings saved.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id={type === null ? 'newCommentTypeModal' : 'editCommentTypeModal'}
      labelledBy={type === null ? 'newCommentTypeModalLabel' : 'editCommentTypeModalLabel'}
      open={open}
      onClose={onClose}
      title={type === null ? 'New comment type' : 'Edit comment type'}
    >
      <form
        id={type === null ? 'new-comment-type-form' : 'edit-type-form'}
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label className="control-label">Type name:</label>
          <input
            type="text"
            className="form-control"
            name="type_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="control-label">Type color:</label>{' '}
          <span className="badge" style={{ minWidth: 20, minHeight: 20, backgroundColor: color }}>
            {' '}
          </span>
          {/* In macheta era o casuta de text cu `colorpicker` pe ea - pluginul
              jQuery care in v4 nu se incarca, deci culoarea se putea doar SCRIE
              de mana, in hexa. `type="color"` e alegerea asezata deja pe Chat
              settings, si trimite acelasi `#rrggbb` pe acelasi camp. */}
          <input
            type="color"
            className="form-control"
            name="type_color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className={type === null ? 'btn btn-primary submit-new-type' : 'btn btn-primary submit-update-type'}
            disabled={name.trim() === '' || save.isPending}
          >
            {type === null ? 'Create' : 'Update'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
